import { useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Plus, Minus } from "lucide-react";

import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import FloatingActions from "@/components/FloatingActions";

const FAQS = [
  {
    category: "Airport Transfers",
    q: "How do you handle airport pickups?",
    a: "We monitor your flight in real time and adjust pickup times for delays at no extra cost. Your chauffeur will meet you inside the arrivals hall with a name sign on request.",
  },
  {
    category: "Airport Transfers",
    q: "Is Meet & Greet included?",
    a: "Meet & Greet is available for all Sydney Airport arrivals — an additional charge applies for the chauffeur to park, walk in and meet you inside the terminal with a name sign.",
  },
  {
    category: "Waiting Time",
    q: "Is there any waiting time included?",
    a: "The first fifteen minutes of waiting are complimentary. Beyond that, a modest per-minute charge applies. Waiting rules differ slightly for pickups leaving Sydney Airport terminals.",
  },
  {
    category: "Bookings",
    q: "Can I change or cancel a booking?",
    a: "Yes. Changes are free up to twelve hours before pickup. Cancellations made more than twenty-four hours in advance are fully refundable. A call or email is usually the quickest way to reach us.",
  },
  {
    category: "Bookings",
    q: "Do you accept short-notice bookings?",
    a: "We accommodate same-day bookings whenever possible, particularly within Greater Sydney. For weekends and public holidays we recommend booking at least twenty-four hours ahead where you can.",
  },
  {
    category: "Pricing",
    q: "How is pricing calculated?",
    a: "Fares are based on vehicle class, distance, duration and time of day, with airport pricing configured separately. Every quote is all-inclusive — no meters, no surge pricing, no hidden fees. GST is applied at the point of booking.",
  },
  {
    category: "Vehicles",
    q: "How is the vehicle chosen?",
    a: "The booking system recommends a class based on passenger count and luggage, and you can adjust the class where relevant. For four passengers we recommend the Luxury SUV; for five or more the Luxury Van.",
  },
  {
    category: "Vehicles",
    q: "Are child seats available?",
    a: "Yes. Infant and child seats are provided on request at no additional charge — please mention this in the booking notes so we can prepare the correct restraint for your journey.",
  },
  {
    category: "Corporate",
    q: "Do you offer corporate accounts?",
    a: "Yes. We provide traveller profiles, consolidated monthly invoicing, cost centres and priority booking for corporate clients. Please get in touch through the concierge desk to set one up.",
  },
];

export default function FAQPage() {
  return (
    <div data-testid="faq-page" className="bg-ivory">
      <Navbar />
      <FAQHero />
      <FAQList />
      <FAQClosing />
      <Footer />
      <FloatingActions />
    </div>
  );
}

/* Hero — minimal, typography-only, no image */
function FAQHero() {
  return (
    <section
      data-testid="faq-hero-section"
      className="relative bg-ivory pt-40 md:pt-48 pb-16 md:pb-20"
    >
      <div className="max-w-[92rem] mx-auto px-6 lg:px-12 grid lg:grid-cols-12 gap-10 items-end">
        <div className="lg:col-span-8">
          <motion.p
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
            className="sjc-eyebrow text-muted-light flex items-center gap-4"
          >
            <span className="sjc-hairline w-8" />
            Frequently Asked
          </motion.p>
          <motion.h1
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, delay: 0.08, ease: [0.16, 1, 0.3, 1] }}
            data-testid="faq-heading"
            className="font-display mt-6 text-charcoal text-4xl sm:text-5xl md:text-6xl lg:text-[80px] leading-[1.02] tracking-[-0.02em]"
          >
            The questions we&apos;re asked{" "}
            <span className="italic font-light">most often.</span>
          </motion.h1>
        </div>
        <motion.p
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, delay: 0.2, ease: [0.16, 1, 0.3, 1] }}
          className="lg:col-span-4 text-muted-light text-base md:text-lg font-light leading-[1.75] max-w-sm md:justify-self-end"
        >
          Everything we&apos;re asked, quietly answered — from flight monitoring to
          corporate accounts. Can&apos;t find what you&apos;re looking for? The concierge
          desk is a phone call away.
        </motion.p>
      </div>
    </section>
  );
}

/* Accordion list — hairline dividers, italic '+' toggle, no cards */
function FAQList() {
  const [openIndex, setOpenIndex] = useState(0);

  return (
    <section
      data-testid="faq-list-section"
      className="bg-ivory pb-24 md:pb-32"
    >
      <div className="max-w-4xl mx-auto px-6 lg:px-8 border-t border-charcoal/15">
        {FAQS.map((f, i) => {
          const open = openIndex === i;
          return (
            <div
              key={i}
              data-testid={`faq-item-${i}`}
              className="border-b border-charcoal/10"
            >
              <button
                type="button"
                data-testid={`faq-trigger-${i}`}
                onClick={() => setOpenIndex(open ? -1 : i)}
                className="w-full flex items-start justify-between gap-8 py-8 md:py-9 text-left group"
              >
                <div className="flex-1 min-w-0">
                  <p className="sjc-eyebrow text-muted-light/70 mb-3">
                    {f.category}
                  </p>
                  <h3
                    className={`font-display text-2xl md:text-[28px] lg:text-[32px] leading-[1.2] tracking-tight transition-colors duration-500 ${
                      open ? "text-charcoal" : "text-charcoal/85 group-hover:text-charcoal"
                    }`}
                  >
                    {f.q}
                  </h3>
                </div>
                <span
                  className={`flex-shrink-0 mt-3 w-9 h-9 border border-charcoal/20 grid place-items-center transition-all duration-500 ${
                    open ? "bg-midnight text-warm-white border-midnight" : "text-charcoal group-hover:border-midnight"
                  }`}
                >
                  {open ? (
                    <Minus className="w-3.5 h-3.5" strokeWidth={1.75} />
                  ) : (
                    <Plus className="w-3.5 h-3.5" strokeWidth={1.75} />
                  )}
                </span>
              </button>

              <AnimatePresence initial={false}>
                {open && (
                  <motion.div
                    key="content"
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: "auto", opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.45, ease: [0.16, 1, 0.3, 1] }}
                    className="overflow-hidden"
                    data-testid={`faq-content-${i}`}
                  >
                    <p className="text-muted-light text-[15px] md:text-[17px] font-light leading-[1.8] pb-9 pr-16 max-w-2xl">
                      {f.a}
                    </p>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          );
        })}
      </div>
    </section>
  );
}

function FAQClosing() {
  return (
    <section
      data-testid="faq-closing-section"
      className="bg-midnight text-warm-white py-24 md:py-32 border-t border-white/10"
    >
      <div className="max-w-3xl mx-auto px-6 lg:px-8 text-center">
        <p className="sjc-eyebrow text-gold flex items-center justify-center gap-4">
          <span className="sjc-hairline w-8" />
          Still have a question?
          <span className="sjc-hairline w-8" />
        </p>
        <h2 className="font-display mt-8 text-4xl md:text-5xl lg:text-[56px] leading-[1.05] tracking-[-0.015em]">
          Call the concierge desk{" "}
          <span className="italic font-light">— any hour, any day.</span>
        </h2>
        <div className="mt-10 flex flex-col sm:flex-row items-center justify-center gap-8">
          <a
            href="tel:+61400000000"
            data-testid="faq-closing-phone"
            className="group inline-flex items-center gap-3 border border-gold/40 text-warm-white px-8 py-4 text-[11px] tracking-[0.28em] uppercase font-medium transition-all duration-500 hover:border-gold hover:bg-gold/[0.05]"
          >
            <span>+61 400 000 000</span>
            <span className="w-1.5 h-1.5 rounded-full bg-gold" />
          </a>
          <Link
            to="/book"
            data-testid="faq-closing-cta"
            className="group inline-flex items-center gap-3 bg-warm-white text-midnight px-8 py-4 text-[11px] tracking-[0.28em] uppercase font-semibold transition-all duration-500 hover:bg-gold"
          >
            <span>Book Your Journey</span>
            <span className="w-2 h-2 rounded-full bg-midnight transition-transform duration-500 group-hover:translate-x-1" />
          </Link>
        </div>
      </div>
    </section>
  );
}
